import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { FormProvider } from "react-hook-form";
import { RHFRadioGroup } from "../../hook-form/RHFRadioGroup";
import { RHFCheckbox } from "../../hook-form/RHFCheckbox";
import { RHFTextField } from "../../hook-form/RHFTextField";
import { RHFDatePicker } from "../../hook-form/RHFDateSelect";
import {
  FoilPeelTestFormValues,
  FoilPeelTestPayload,
} from "../../../types/foilPeelTestTypes";
import { postFoilPeelTest } from "../../../services/foilPeelTestService";
import { useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import { useFoilPeelTestForm } from "../../../hooks/useFoilPeelTestForm";

interface Props {
  stationId: number;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const ADHESION_KEYS = ["G01", "G02", "G03", "G04", "G05", "G06", "G07"];

const LAMINATOR_OPTIONS = [
  { label: "Laminator 1 (JT)", value: 26 },
  { label: "Laminator 2 (NG)", value: 28 },
  { label: "Laminator 3 (NG)", value: 56 },
];

const LAMINATOR_PROGRAM_OPTIONS = [
  { label: "GBS", value: "GBS" },
  { label: "andere", value: "andere" },
];

const TEST_TYPE_OPTIONS = [
  { label: "Produktionstest", value: "1" },
  { label: "WEK EVA", value: "2" },
  { label: "WEK Backsheet", value: "3" },
  { label: "WEK Glas", value: "4" },
];

const POSITION_OPTIONS = [
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4", value: 4 },
];

const BACKSHEET_TYPE_OPTIONS = [
  { label: "Cynagard205A", value: "Cynagard205A" },
  { label: "andere", value: "andere" },
];

const FRONTGLASS_THICKNESS_OPTIONS = [
  { label: "5mm", value: "5" },
  { label: "andere", value: "andere" },
];

const EVA_TYPE_OPTIONS = [
  { label: "First EVA F406PS", value: "First EVA F406PS" },
  { label: "andere", value: "andere" },
];

export function FoilPeelTestForm({ stationId, onError, onSuccess }: Props) {
  const methods = useFoilPeelTestForm();

  const { control, watch, handleSubmit, reset, setError } = methods;
  const [successOpen, setSuccessOpen] = useState(false);

  const laminatorProgram = watch("laminatorRecipe");
  const backsheetType = watch("backsheetType");
  const evaFoilType = watch("evaFoilType");
  const frontGlassThickness = watch("frontGlassThickness");

  function resolveOtherValues(data: FoilPeelTestFormValues) {
    let resolvedLaminatorRecipe = data.laminatorRecipe;
    let resolvedBacksheetType = data.backsheetType;
    let resolvedEvaFoilType = data.evaFoilType;
    let resolvedFrontGlassThickness = data.frontGlassThickness;

    if (resolvedLaminatorRecipe === "andere") {
      const v = data.laminatorRecipeOther?.trim();
      if (!v) {
        setError("laminatorRecipeOther", {
          type: "required",
          message: "Bitte geben Sie ein Lami Programm an.",
        });
        return undefined;
      }
      resolvedLaminatorRecipe = v;
    }

    if (resolvedBacksheetType === "andere") {
      const v = data.backsheetTypeOther?.trim();
      if (!v) {
        setError("backsheetTypeOther", {
          type: "required",
          message: "Bitte geben Sie einen Backsheet Typ an.",
        });
        return undefined;
      }
      resolvedBacksheetType = v;
    }

    if (resolvedEvaFoilType === "andere") {
      const v = data.evaFoilTypeOther?.trim();
      if (!v) {
        setError("evaFoilTypeOther", {
          type: "required",
          message: "Bitte geben Sie einen EVA Typ an.",
        });
        return undefined;
      }
      resolvedEvaFoilType = v;
    }

    if (resolvedFrontGlassThickness === "andere") {
      const v = data.frontGlassThicknessOther?.toString().trim();
      if (!v) {
        setError("frontGlassThicknessOther", {
          type: "required",
          message: "Bitte geben Sie eine Frontglasstärke an.",
        });
        return undefined;
      }
      resolvedFrontGlassThickness = v as any;
    }

    return {
      laminatorRecipe: resolvedLaminatorRecipe,
      backsheetType: resolvedBacksheetType,
      evaFoilType: resolvedEvaFoilType,
      frontGlassThickness: resolvedFrontGlassThickness,
    } as const;
  }

  const onSubmit = async (data: FoilPeelTestFormValues) => {
    await executeSubmit(data);
  };

  const executeSubmit = async (data: FoilPeelTestFormValues) => {
    const resolved = resolveOtherValues(data);
    if (!resolved) return;

    const payload: FoilPeelTestPayload = {
      stationid: stationId,
      dateInProduction: new Date(),
      testType: data.testType,
      testForArticle: data.testForArticle,
      isGlassCleaned: data.isGlassCleaned,
      laminatorStationId: Number(data.laminatorStationId),
      laminatorRecipe: String(resolved.laminatorRecipe),
      laminatorPosition: Number(data.laminatorPosition),
      frontGlassThickness: Number(resolved.frontGlassThickness),
      frontGlassBatchNumber: data.frontGlassBatchNumber,
      frontGlassManufacturingDate: data.frontGlassManufacturingDate,
      evaFoilType: String(resolved.evaFoilType),
      evaFoilBatchNumber: data.evaFoilBatchNumber,
      evaFoilManufacturingDate: new Date(),
      backsheetType: String(resolved.backsheetType),
      backsheetTypeBatchNumber: data.backsheetTypeBatchNumber,
      adhesionEva: data.adhesionEva,
      adhesionBacksheet: data.adhesionBacksheet,
    };

    try {
      await postFoilPeelTest(payload);
      reset();
      setSuccessOpen(true);
      onSuccess?.();
    } catch (error: any) {
      onError?.(error?.message || "Ein Fehler ist aufgetreten");
    }
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        display="flex"
        flexDirection="column"
        gap={3}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack spacing={2}>
          <RHFCheckbox
            name="isGlassCleaned"
            label="Glas gereinigt?"
            control={control}
          />

          <RHFTextField
            name="testForArticle"
            label="Hafttest entspricht"
            control={control}
            required
          />

          <RHFRadioGroup
            name="testType"
            label="Laminatortest"
            control={control}
            options={TEST_TYPE_OPTIONS}
            row
            required
          />

          <RHFRadioGroup
            name="laminatorStationId"
            label="Laminator"
            control={control}
            options={LAMINATOR_OPTIONS}
            row
            required
          />

          <RHFRadioGroup
            name="laminatorPosition"
            label="Position auf dem Einfuhrtisch"
            control={control}
            options={POSITION_OPTIONS}
            row
            required
          />

          <RHFRadioGroup
            name="laminatorRecipe"
            label="Lami Programm"
            control={control}
            options={LAMINATOR_PROGRAM_OPTIONS}
            row
            required
          />
          {laminatorProgram === "andere" && (
            <RHFTextField
              name="laminatorRecipeOther"
              label="Lami Programm (andere)"
              control={control}
              required
            />
          )}

          <RHFRadioGroup
            name="backsheetType"
            label="Backsheet Typ"
            control={control}
            options={BACKSHEET_TYPE_OPTIONS}
            row
            required
          />
          {backsheetType === "andere" && (
            <RHFTextField
              name="backsheetTypeOther"
              label="Backsheet Typ (andere)"
              control={control}
              required
            />
          )}

          <RHFTextField
            name="backsheetTypeBatchNumber"
            label="Backsheet Charge Nr."
            control={control}
            required
          />

          <RHFRadioGroup
            name="evaFoilType"
            label="EVA Typ"
            control={control}
            options={EVA_TYPE_OPTIONS}
            row
            required
          />
          {evaFoilType === "andere" && (
            <RHFTextField
              name="evaFoilTypeOther"
              label="EVA Typ (andere)"
              control={control}
              required
            />
          )}

          <RHFTextField
            name="evaFoilBatchNumber"
            label="EVA Charge Nr."
            control={control}
            required
          />

          <RHFRadioGroup
            name="frontGlassThickness"
            label="Frontglass Dicke"
            control={control}
            options={FRONTGLASS_THICKNESS_OPTIONS}
            row
            required
          />
          {frontGlassThickness === "andere" && (
            <RHFTextField
              name="frontGlassThicknessOther"
              label="Frontglass Dicke (andere)"
              control={control}
              type="number"
              required
            />
          )}
          <RHFTextField
            name="frontGlassBatchNumber"
            label="Frontglas Charge Nr."
            control={control}
            required
          />

          <RHFDatePicker
            name="frontGlassManufacturingDate"
            label="Frontglass Produktionsdatum"
            control={control}
          />

          <h3>Haftest [N]</h3>
          <Box display="flex" gap={2}>
            <Box>
              <Typography fontWeight="bold">Glas (mind. 80N):</Typography>
              {ADHESION_KEYS.map((key) => (
                <RHFTextField
                  key={key}
                  name={`adhesionEva.${key}`}
                  type="number"
                  control={control}
                  size="small"
                  fullWidth
                />
              ))}
            </Box>

            <Box>
              <Typography fontWeight="bold">Backsheet (mind. 60N):</Typography>
              {ADHESION_KEYS.map((key) => (
                <RHFTextField
                  key={key}
                  name={`adhesionBacksheet.${key}`}
                  type="number"
                  control={control}
                  size="small"
                  fullWidth
                />
              ))}
            </Box>
          </Box>
          <Button variant="contained" type="submit">
            Speichern
          </Button>
        </Stack>
      </Box>

      <Snackbar
        open={successOpen}
        autoHideDuration={6000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Der Eintrag wurde gespeichert!
        </Alert>
      </Snackbar>
    </FormProvider>
  );
}
